/**
 * @file Single collection import interface component
 * @version 1.0.0
 * @author Claude Assistant
 * @since 2025-07-09
 */

import { useDataPortability } from '../composables/dataPortability.js';
import { useNotifications } from '../../core/errors.js';

const { ref, computed, watch } = Vue;

export default {
  name: 'SingleCollectionImport',

  template: `
    <div class="single-collection-import">
      <div class="mb-3">
        <label class="form-label">Import Strategy</label>
        <select class="form-select" v-model="importStrategy">
          <option value="upsert">Upsert (Update existing, add new)</option>
          <option value="replace">Replace (Clear collection first)</option>
        </select>
      </div>
      
      <div class="d-flex gap-2">
        <button 
          class="btn btn-primary" 
          @click="executeImport"
          :disabled="isImporting || !importData"
        >
          <span v-if="isImporting" class="spinner-border spinner-border-sm me-2" role="status"></span>
          <i v-else class="bi-upload me-2"></i>
          {{ isImporting ? 'Importing...' : 'Import Data' }}
        </button>
        <button 
          class="btn btn-secondary" 
          @click="cancel"
          v-if="!isImporting"
        >
          Cancel
        </button>
        <button 
          class="btn btn-warning" 
          @click="skip"
          v-if="isImporting && showSkipButton"
        >
          <i class="bi-skip-forward me-2"></i>
          Skip
        </button>
      </div>
      
      <!-- Import results -->
      <div v-if="importResults" class="mt-3">
        <div class="alert" :class="importResultsAlertClass">
          <h6>Import Results</h6>
          <div class="row">
            <div class="col-md-4">
              <strong>{{ importResults.summary.successful }}</strong> imported
            </div>
            <div class="col-md-4">
              <strong>{{ importResults.summary.failed }}</strong> failed
            </div>
            <div class="col-md-4">
              <strong>{{ importResults.summary.total }}</strong> total
            </div>
          </div>
          
          <div v-if="importResults.errors.length > 0" class="mt-2">
            <details>
              <summary>View Errors ({{ importResults.errors.length }})</summary>
              <div class="mt-2">
                <div v-for="error in importResults.errors" :key="error.id" class="small text-danger">
                  <strong>{{ error.id }}:</strong> {{ error.message }}
                </div>
              </div>
            </details>
          </div>
        </div>
      </div>
    </div>
  `,

  props: {
    collectionName: {
      type: String,
      required: true,
    },
    importData: {
      type: Object,
      required: true,
    },
    showSkipButton: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['import-complete', 'import-skip', 'cancel'],

  setup(props, { emit }) {
    const { importCollection, isImporting } = useDataPortability();
    const { success, error, warning } = useNotifications();

    const importStrategy = ref('upsert');
    const importResults = ref(null);

    const importResultsAlertClass = computed(() => {
      if (!importResults.value) return 'alert-info';
      const { successful, failed } = importResults.value.summary;
      if (failed === 0) return 'alert-success';
      if (successful === 0) return 'alert-danger';
      return 'alert-warning';
    });

    const executeImport = async () => {
      if (!props.importData) return;

      try {
        importResults.value = null;

        const result = await importCollection(
          props.collectionName,
          props.importData,
          { strategy: importStrategy.value }
        );

        importResults.value = result;

        // Show success notification
        if (result.summary.failed === 0) {
          success(`${result.summary.successful} records imported successfully`);
        } else {
          warning(
            `${result.summary.successful} records imported, ${result.summary.failed} failed`
          );
        }

        emit('import-complete', result);
      } catch (err) {
        error(`Import failed: ${err.message || 'Unknown error'}`);
        emit('import-complete', { success: false, error: err.message });
      }
    };

    const cancel = () => {
      importResults.value = null;
      emit('cancel');
    };

    const skip = () => {
      emit('import-skip');
    };

    // Reset results when import data changes
    watch(
      () => props.importData,
      () => {
        importResults.value = null;
      }
    );

    return {
      importStrategy,
      importResults,
      importResultsAlertClass,
      executeImport,
      cancel,
      skip,
      isImporting,
    };
  },
};
